<template shadowrootmode="closed">
  <AppWrap
    :theme="props.dataTheme ?? 'light'"
    :view="props.dataView ?? 'Main'"
    :courseId="props.dataCourse_id ?? null"
    :categoryId="props.dataCategory_id ?? null"
    :accent="props.dataAccent ?? ''"
    :font="props.dataFont ?? ''"
    :overlay="props.dataOverlay === '1' || props.dataOverlay === 'true' || props.dataOverlay === true"
  ></AppWrap>
</template>

<script setup lang="ts">
import { provide } from "vue";
import i18n from "./plugins/i18n.ts";
import AppWrap from "./components/AppWrap/index.ts";
import type { IAppConfigProvider } from "./application/interfaces/IAppConfigProvider.ts";
import type { IAccessTokenProvider } from "./application/interfaces/IAccessTokenProvider.ts";
import type { ICategoryRepository } from "./application/interfaces/ICategoryRepository.ts";
import type { ICourseRepository } from "./application/interfaces/ICourseRepository.ts";
import type { ITokenApi } from "./application/interfaces/ITokenApi.ts";
import type { IWhoamiApi } from "./application/interfaces/IWhoamiApi.ts";
import type { ICartReferenceProvider } from "./application/interfaces/ICartReferenceProvider.ts";
import type { ICartRepository } from "./application/interfaces/ICartRepository.ts";
import type { IVideoRepository } from "./application/interfaces/IVideoRepository.ts";
import type { ILoginApi } from "./application/interfaces/ILoginApi.ts";
import type { IAddressApi } from "./application/interfaces/IAddressApi.ts";
import type { ICustomerApi } from "./application/interfaces/ICustomerApi.ts";
import { ConfigProvider } from "./infrastructure/config/ConfigProvider.ts";
import { LocalStorageTokenProvicer } from "./infrastructure/api/LocalStorageTokenProvider.ts";
import { CategoryRepository } from "./infrastructure/api/CategoryRepository.ts";
import { CourseRepository } from "./infrastructure/api/CourseRepository.ts";
import { TokenApi } from "./infrastructure/api/TokenApi.ts";
import { WhoamiApi } from "./infrastructure/api/WhoamiApi.ts";
import { LoginApi } from "./infrastructure/api/LoginApi.ts";
import { AddressApi } from "./infrastructure/api/AddressApi.ts";
import { CustomerApi } from "./infrastructure/api/CustomerApi.ts";
import { CategoryService } from "./application/services/CategoryService.ts";
import { CourseService } from "./application/services/CourseService.ts";
import { UserService } from "./application/services/UserService.ts";
import { LoginService } from "./application/services/LoginService.ts";
import { AddressService } from "./application/services/AddressService.ts";
import { CustomerService } from "./application/services/CustomerService.ts";
import { LocalStorageCartReferenceProvider } from "./infrastructure/api/LocalStorageCartReferenceProvider.ts";
import { CartRepository } from "./infrastructure/api/CartRepository.ts";
import { CartService } from "./application/services/CartService.ts";
import type { IUrlStore } from "./application/interfaces/IUrlStore.ts";
import { LocalStorageUrlStore } from "./infrastructure/api/LocalStorageUrlStore.ts";
import { VideoRepository } from "./infrastructure/api/VideoRepository.ts";
import { VideoService } from "./application/services/VideoService.ts";

// Attribute der Webkomponente (data-* im HTML)
const props = defineProps({
  dataApi_key: String,
  dataApi_url: String, // default: https://api.tanzschulmanager.net
  dataTheme: String, // light/dark
  dataView: String, // Steuert spezifische Ansichten (default: Main)
  dataCourse_id: Number, // Triggert die Ansicht des Checkouts
  dataCategory_id: Number, // Triggert die Ansicht nur einer Kategorie
  dataAccent: String, // optional: Akzentfarbe der Tanzschule, z. B. "#e30613"
  dataFont: String, // optional: Schriftfamilie der Website, z. B. "Open Sans, sans-serif"
  dataOverlay: [String, Boolean], // optional: "1" = Checkout als eigener Dialog ueber der Website (data-view="Checkout")
});

// provide the i18n plugin
provide("i18n", i18n);

const appConfigProvider: IAppConfigProvider = new ConfigProvider();
const tokenApi: ITokenApi = new TokenApi(appConfigProvider);
const tokenProvider: IAccessTokenProvider = new LocalStorageTokenProvicer(tokenApi);
const urlStore: IUrlStore = new LocalStorageUrlStore();
const cartReferenceProvider: ICartReferenceProvider = new LocalStorageCartReferenceProvider();
const cartRepository: ICartRepository = new CartRepository(appConfigProvider, tokenProvider);
const categoryRepository: ICategoryRepository = new CategoryRepository(appConfigProvider, tokenProvider);
const courseRepository: ICourseRepository = new CourseRepository(appConfigProvider, tokenProvider);
const videoRepository: IVideoRepository = new VideoRepository(appConfigProvider, tokenProvider);
const whoamiApi: IWhoamiApi = new WhoamiApi(appConfigProvider, tokenProvider);
const loginApi: ILoginApi = new LoginApi(appConfigProvider);
const addressApi: IAddressApi = new AddressApi(appConfigProvider);
const customerApi: ICustomerApi = new CustomerApi(appConfigProvider, tokenProvider);

const cartService: CartService = new CartService(cartRepository, cartReferenceProvider);
const categoryService: CategoryService = new CategoryService(categoryRepository);
const courseService: CourseService = new CourseService(courseRepository);
const userService: UserService = new UserService(whoamiApi, tokenApi, tokenProvider, urlStore);
const videoService: VideoService = new VideoService(videoRepository);
const loginService: LoginService = new LoginService(loginApi, tokenProvider);
const addressService: AddressService = new AddressService(addressApi);
const customerService: CustomerService = new CustomerService(customerApi);

// use provide/inject to pass application services to components
provide("categoryService", categoryService);
provide("courseService", courseService);
provide("userService", userService);
provide("cartService", cartService);
provide("videoService", videoService);
provide("loginService", loginService);
provide("addressService", addressService);
provide("customerService", customerService);

appConfigProvider.initConfig(
  props.dataApi_url ? props.dataApi_url : "https://api.tanzschulmanager.net",
  props.dataApi_key ? props.dataApi_key : "",
);
</script>
